import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner, Alert, Table } from "react-bootstrap";

const AddMemberModal = ({ show, onClose, projectId, onMemberAdded }) => {
  const [newMember, setNewMember] = useState({
    email: "",
    role: "Contributor",
  });
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const token = localStorage.getItem("auth_token");

  useEffect(() => {
    if (show) fetchMembers();
  }, [show]);

  const fetchMembers = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `https://loopsneller.com/api/projects/${projectId}/members`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json();
      setMembers(data);
    } catch (err) {
      console.error("Error fetching members:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    setAdding(true);
    setError(null);
    setSuccess(null);

    try {
      const userRes = await fetch(
        `https://loopsneller.com/api/users/by-email?email=${encodeURIComponent(
          newMember.email
        )}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!userRes.ok) {
        const errorData = await userRes.json();
        throw new Error(errorData.error || "User not registered");
      }

      const user = await userRes.json();

      const res = await fetch(
        `https://loopsneller.com/api/projects/${projectId}/members`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ user_id: user.id, role: newMember.role }),
        }
      );

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to add member");
      }

      setSuccess("New member added successfully!");
      setNewMember({ email: "", role: "Contributor" });
      fetchMembers();
      onMemberAdded?.();
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setAdding(false);
    }
  };

  const handleRoleUpdate = async (userId, newRole) => {
    setUpdating(userId);
    try {
      const res = await fetch(
        `https://loopsneller.com/api/projects/${projectId}/members/${userId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ role: newRole }),
        }
      );
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to update role");
      }
      setSuccess("Role updated successfully!");
      fetchMembers();
      onMemberAdded?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(null);
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this member?")) return;

    setRemoving(userId);
    try {
      const res = await fetch(
        `https://loopsneller.com/api/projects/${projectId}/members/${userId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to remove member");
      }
      setSuccess("Member removed successfully!");
      fetchMembers();
      onMemberAdded?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Manage Project Members</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h5>Add New Member</h5>
        <Form.Group className="mb-2">
          <Form.Label>Email</Form.Label>
          <Form.Control
            type="email"
            value={newMember.email}
            onChange={(e) =>
              setNewMember({ ...newMember, email: e.target.value })
            }
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Role</Form.Label>
          <Form.Select
            value={newMember.role}
            onChange={(e) =>
              setNewMember({ ...newMember, role: e.target.value })
            }
          >
            <option>Manager</option>
            <option>Contributor</option>
            <option>Viewer</option>
          </Form.Select>
        </Form.Group>
        <Button
          variant="outline-secondary"
          onClick={handleAddMember}
          disabled={adding}
        >
          {adding ? <Spinner size="sm" /> : "Add Member"}
        </Button>

        {success && (
          <Alert variant="success" className="mt-3">
            {success}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}

        <hr />

        <h5>Existing Members</h5>
        {loading ? (
          <Spinner animation="border" />
        ) : (
          <Table striped bordered hover responsive size="sm">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Update Role</th>
                <th>Remove</th>
              </tr>
            </thead>
            <tbody>
              {members.map((m) => (
                <tr key={m.user.id}>
                  <td>{m.user.name}</td>
                  <td>{m.user.email}</td>
                  <td>{m.role}</td>
                  <td>
                    <Form.Select
                      value={m.role}
                      onChange={(e) =>
                        handleRoleUpdate(m.user.id, e.target.value)
                      }
                      disabled={updating === m.user.id}
                    >
                      <option>Manager</option>
                      <option>Contributor</option>
                      <option>Viewer</option>
                    </Form.Select>
                  </td>
                  <td>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemoveMember(m.user.id)}
                      disabled={removing === m.user.id}
                    >
                      {removing === m.user.id ? (
                        <Spinner animation="border" size="sm" />
                      ) : (
                        "Remove"
                      )}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default AddMemberModal;
